/**
 * One formatting rule per metric, and one rule for its change.
 *
 * The dashboard printed the same reading several ways: body fat 23.7 % in the
 * hero beside 24 % on the Measurements card and "Now 24 %" on the Target page;
 * a waist change of −5 in the hero beside −4 on the Avatar tape list. Each
 * surface rounded on its own — some before subtracting, some after — and
 * body-fat changes were written as "%" in one place and "pts" in another.
 * Every surface now formats through here.
 *
 *   body fat   one decimal, "23.7 %"; a change is in points, "−3.0 pts"
 *   weight     one decimal, "90.0 kg"; a change is "−4.0 kg"
 *   lengths    whole centimetres, "97 cm"; a change is "−5 cm"
 *
 * A change is taken between the figures as displayed, not the raw values:
 * a waist shown as 102 cm then 97 cm reads −5, because −4 beside those two
 * numbers does not add up. Lives in design-system/ (no app imports) so the
 * cards here can use it as well as lib/ and the pages.
 */

export type MetricKind = 'body_fat' | 'weight' | 'length';

const MINUS = '−';

export const metricKind = (key: string): MetricKind =>
  key === 'body_fat' ? 'body_fat' : key === 'weight' ? 'weight' : 'length';

/** Decimal places a metric is displayed at. */
const digitsOf = (kind: MetricKind) => (kind === 'length' ? 0 : 1);

/** A value rounded the way it is displayed. */
export const roundMetric = (key: string, value: number): number => {
  const f = 10 ** digitsOf(metricKind(key));
  return Math.round(value * f) / f;
};

export const unitOf = (key: string): string => {
  const kind = metricKind(key);
  return kind === 'body_fat' ? '%' : kind === 'weight' ? 'kg' : 'cm';
};

/** Unit of a change: body fat moves in points, not percent. */
export const changeUnitOf = (key: string): string =>
  metricKind(key) === 'body_fat' ? 'pts' : unitOf(key);

/** The number alone, e.g. "23.7", "90.0", "97". */
export const formatMetricNumber = (key: string, value: number): string =>
  roundMetric(key, value).toFixed(digitsOf(metricKind(key)));

/** The number and its unit, e.g. "23.7 %". */
export const formatMetric = (key: string, value: number): string =>
  `${formatMetricNumber(key, value)} ${unitOf(key)}`;

/**
 * Later minus earlier, between the figures as displayed. Negative is a
 * reduction.
 */
export const metricChange = (key: string, earlier: number, later: number): number =>
  roundMetric(key, roundMetric(key, later) - roundMetric(key, earlier));

/** Signed with a real minus, an explicit plus, and never a signed zero. */
export const formatSigned = (n: number, digits: number): string => {
  const text = Math.abs(n).toFixed(digits);
  if (Number(text) === 0) return text;
  return `${n > 0 ? '+' : MINUS}${text}`;
};

/** The signed number alone, e.g. "−3.0", "−5". */
export const formatChangeNumber = (key: string, change: number): string =>
  formatSigned(change, digitsOf(metricKind(key)));

/** The signed change and its unit, e.g. "−3.0 pts", "−5 cm". */
export const formatChange = (key: string, change: number): string =>
  `${formatChangeNumber(key, change)} ${changeUnitOf(key)}`;

/** Shorthands for the three metrics the dashboard repeats most. */
export const formatBodyFat = (percent: number) => formatMetric('body_fat', percent);
export const formatWeight = (kg: number) => formatMetric('weight', kg);
export const formatCm = (cm: number) => formatMetric('waist', cm);

/** "26 Mar", for a value carried from an earlier scan. */
export const shortDate = (d: Date): string =>
  d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/** "90.0 kg · 26 Mar" when the value is from an earlier scan than the one shown. */
export const withAsOf = (text: string, asOf?: Date): string =>
  asOf ? `${text} · ${shortDate(asOf)}` : text;
